/**
 * Mirrors the `max_length` on interlude's `IpAllowlistIn.ip_allowlist`.
 * Every line sent counts, repeats included.
 */
export const IP_ALLOWLIST_MAX_ENTRIES = 50;

/**
 * Interlude parses each entry as a Python `IPv4Network | IPv6Network` in strict
 * mode, then rejects what Kong cannot match. Whatever it rejects comes back as
 * a 400 carrying no error code this app maps, so the modal has to catch every
 * one of these first. The parsing below follows CPython's `ipaddress` rule for
 * rule; anything looser or stricter is a divergence from the backend.
 */
export type IpAllowlistEntryViolation =
  | "malformed"
  | "hostBitsSet"
  | "zoneId"
  | "ipv4Mapped";

const DECIMAL = /^[0-9]+$/;
const HEXTET = /^[0-9a-fA-F]{1,4}$/;
const IPV4_ALL_ONES = 0xffffffff;
const IPV6_HEXTET_COUNT = 8;

function parseOctet(octet: string): number | null {
  if (!DECIMAL.test(octet) || octet.length > 3) return null;
  if (octet !== "0" && octet.startsWith("0")) return null;
  const value = Number(octet);
  return value > 255 ? null : value;
}

function parseIpv4(address: string): number | null {
  const octets = address.split(".");
  if (octets.length !== 4) return null;
  let value = 0;
  for (const octet of octets) {
    const parsed = parseOctet(octet);
    if (parsed === null) return null;
    value = value * 256 + parsed;
  }
  return value;
}

function ipv4Netmask(prefixLength: number): number {
  return prefixLength === 0 ? 0 : (IPV4_ALL_ONES << (32 - prefixLength)) >>> 0;
}

function ipv4PrefixFromNetmask(mask: number): number | null {
  for (let prefixLength = 0; prefixLength <= 32; prefixLength++) {
    if (ipv4Netmask(prefixLength) === mask) return prefixLength;
  }
  return null;
}

/** A prefix length, or a netmask, or a hostmask, in that order. */
function parseIpv4Prefix(prefix: string): number | null {
  if (DECIMAL.test(prefix)) {
    const prefixLength = Number(prefix);
    return prefixLength <= 32 ? prefixLength : null;
  }
  const mask = parseIpv4(prefix);
  if (mask === null) return null;
  return (
    ipv4PrefixFromNetmask(mask) ??
    ipv4PrefixFromNetmask((mask ^ IPV4_ALL_ONES) >>> 0)
  );
}

function validateIpv4Network(
  address: string,
  prefix: string | undefined,
): IpAllowlistEntryViolation | null {
  const value = parseIpv4(address);
  const prefixLength = prefix === undefined ? 32 : parseIpv4Prefix(prefix);
  if (value === null || prefixLength === null) return "malformed";
  const hostBits = (value & ~ipv4Netmask(prefixLength)) >>> 0;
  return hostBits === 0 ? null : "hostBitsSet";
}

function parseIpv6(address: string): bigint | null {
  const parts = address.split(":");
  if (parts.length < 3) return null;
  const last = parts.at(-1);
  if (last?.includes(".")) {
    const ipv4 = parseIpv4(last);
    if (ipv4 === null) return null;
    parts.splice(
      -1,
      1,
      (ipv4 >>> 16).toString(16),
      (ipv4 & 0xffff).toString(16),
    );
  }
  if (parts.length > IPV6_HEXTET_COUNT + 1) return null;

  const emptyInner = parts
    .slice(1, -1)
    .flatMap((part, index) => (part === "" ? [index + 1] : []));
  if (emptyInner.length > 1) return null;
  const skipIndex = emptyInner[0];

  let high: Array<string>;
  let low: Array<string>;
  if (skipIndex === undefined) {
    if (parts.length !== IPV6_HEXTET_COUNT) return null;
    high = parts;
    low = [];
  } else {
    high = parts.slice(0, skipIndex);
    low = parts.slice(skipIndex + 1);
    // A leading or trailing empty part is only legal as half of the `::`.
    if (high[0] === "") {
      if (high.length !== 1) return null;
      high = [];
    }
    if (low.at(-1) === "") {
      if (low.length !== 1) return null;
      low = [];
    }
    if (high.length + low.length >= IPV6_HEXTET_COUNT) return null;
  }

  const hextets = [
    ...high,
    ...Array<string>(IPV6_HEXTET_COUNT - high.length - low.length).fill("0"),
    ...low,
  ];
  let value = 0n;
  for (const hextet of hextets) {
    if (!HEXTET.test(hextet)) return null;
    value = (value << 16n) | BigInt(`0x${hextet}`);
  }
  return value;
}

/**
 * Python checks the zone id's shape while parsing and rejects a well-formed
 * one only afterwards, in interlude's validator — after the host-bits check.
 */
function validateIpv6Network(
  address: string,
  prefix: string | undefined,
): IpAllowlistEntryViolation | null {
  const zoneSeparator = address.indexOf("%");
  const zoneId =
    zoneSeparator === -1 ? undefined : address.slice(zoneSeparator + 1);
  if (zoneId !== undefined && (zoneId === "" || zoneId.includes("%"))) {
    return "malformed";
  }
  const value = parseIpv6(
    zoneSeparator === -1 ? address : address.slice(0, zoneSeparator),
  );
  const prefixLength =
    prefix === undefined ? 128 : DECIMAL.test(prefix) ? Number(prefix) : null;
  if (value === null || prefixLength === null || prefixLength > 128) {
    return "malformed";
  }
  const hostMask = (1n << BigInt(128 - prefixLength)) - 1n;
  if ((value & hostMask) !== 0n) return "hostBitsSet";
  if (zoneId !== undefined) return "zoneId";
  if (value >> 32n === 0xffffn) return "ipv4Mapped";
  return null;
}

/**
 * Validate one trimmed allowlist entry against interlude's rules.
 *
 * @returns the rule that was broken, or `null` when interlude accepts it.
 */
export function validateIpAllowlistEntry(
  entry: string,
): IpAllowlistEntryViolation | null {
  const parts = entry.split("/");
  if (parts.length > 2) return "malformed";
  const [address = "", prefix] = parts;
  // Python tries IPv4 first; no IPv4 form contains a colon and every IPv6
  // form does, so the colon picks the parser that can succeed.
  return address.includes(":")
    ? validateIpv6Network(address, prefix)
    : validateIpv4Network(address, prefix);
}
